import React from 'react';

export type NamedFunctionTag = {
  name?: string;
  value?: string;
};

export type FunctionTagSuggestion = {
  key: string;
  label: string;
  value: string;
  insertToken: string;
};

export type FunctionTagSuggestionGroup = {
  key: 'attribute' | 'tool' | 'existing';
  title: string;
  color: 'blue' | 'purple' | 'default';
  tags: FunctionTagSuggestion[];
};

interface UseFunctionTagSuggestionsOptions {
  availableFunctionTags?: string[];
  attributeFunctionTags?: NamedFunctionTag[];
  toolFunctionTags?: NamedFunctionTag[];
  getFunctionText: () => string | null | undefined;
  setFunctionText: (value: string) => void;
}

const buildExistingFunctionTagSuggestions = (
  tags?: string[],
): FunctionTagSuggestion[] => {
  if (!Array.isArray(tags)) {
    return [];
  }
  const map = new Map<string, FunctionTagSuggestion>();
  tags.forEach((tag) => {
    const trimmed = String(tag ?? '').trim();
    if (!trimmed) {
      return;
    }
    const key = trimmed.toUpperCase();
    if (!map.has(key)) {
      map.set(key, {
        key: `existing-${key}`,
        label: trimmed,
        value: trimmed,
        insertToken: `<@T:${trimmed}@>`,
      });
    }
  });
  return Array.from(map.values());
};

const buildNamedFunctionTagSuggestions = (
  items: NamedFunctionTag[] | undefined,
  options: { prefix: 'attribute' | 'tool'; insertPrefix: 'P' | 'B' },
): FunctionTagSuggestion[] => {
  if (!Array.isArray(items)) {
    return [];
  }
  const map = new Map<string, FunctionTagSuggestion>();
  items.forEach((item) => {
    const rawValue = String(item?.value ?? '').trim();
    if (!rawValue) {
      return;
    }
    const label = String(item?.name ?? '').trim() || rawValue;
    const key = rawValue.toUpperCase();
    if (!map.has(key)) {
      map.set(key, {
        key: `${options.prefix}-${key}`,
        label,
        value: rawValue,
        insertToken: `<@${options.insertPrefix}:${rawValue}@>`,
      });
    }
  });
  return Array.from(map.values());
};

export const useFunctionTagSuggestions = ({
  availableFunctionTags,
  attributeFunctionTags,
  toolFunctionTags,
  getFunctionText,
  setFunctionText,
}: UseFunctionTagSuggestionsOptions) => {
  const existingFunctionTagSuggestions = React.useMemo(
    () => buildExistingFunctionTagSuggestions(availableFunctionTags),
    [availableFunctionTags],
  );

  const attributeTagSuggestions = React.useMemo(
    () => buildNamedFunctionTagSuggestions(attributeFunctionTags, { prefix: 'attribute', insertPrefix: 'P' }),
    [attributeFunctionTags],
  );

  const toolTagSuggestions = React.useMemo(
    () => buildNamedFunctionTagSuggestions(toolFunctionTags, { prefix: 'tool', insertPrefix: 'B' }),
    [toolFunctionTags],
  );

  const functionTagGroups = React.useMemo<FunctionTagSuggestionGroup[]>(
    () =>
      [
        {
          key: 'attribute' as const,
          title: 'Parametre Sabit Değerleri',
          color: 'blue' as const,
          tags: attributeTagSuggestions,
        },
        {
          key: 'tool' as const,
          title: 'Bileşen Sabit Değerleri',
          color: 'purple' as const,
          tags: toolTagSuggestions,
        },
        {
          key: 'existing' as const,
          title: 'Diğer Fonksiyon Etiketleri',
          color: 'default' as const,
          tags: existingFunctionTagSuggestions,
        },
      ].filter((group) => group.tags.length > 0),
    [attributeTagSuggestions, toolTagSuggestions, existingFunctionTagSuggestions],
  );

  const appendFunctionTag = React.useCallback(
    (tagValue: string) => {
      const normalized = String(tagValue ?? '').trim();
      if (!normalized) {
        return;
      }
      const existingRaw = getFunctionText?.();
      const existing = existingRaw === undefined || existingRaw === null ? '' : String(existingRaw);
      const separator = existing && !existing.endsWith(' ') ? ' ' : '';
      const nextValue = `${existing}${separator}${normalized}`;
      setFunctionText(nextValue);
    },
    [getFunctionText, setFunctionText],
  );

  return {
    functionTagGroups,
    appendFunctionTag,
  };
};

export default useFunctionTagSuggestions;
